import { jsx } from "react/jsx-runtime";
import CancelIcon from "@mui/icons-material/Cancel.js";
import CheckCircleIcon from "@mui/icons-material/CheckCircle.js";
import EventBusyIcon from "@mui/icons-material/EventBusy.js";
import Link from "@mui/material/Link/index.js";
import Pagination from "@mui/material/Pagination/index.js";
import { styled } from "@mui/material/styles";
import TableCell from "@mui/material/TableCell/index.js";
import TableHead from "@mui/material/TableHead/index.js";
import Typography from "@mui/material/Typography/index.js";
import "@mui/material";
import { FirstPageIcon } from "../../../../../components/icons/FirstPageIcon/FirstPageIcon.js";
import { LastPageIcon } from "../../../../../components/icons/LastPageIcon/LastPageIcon.js";
import { NextPageIcon } from "../../../../../components/icons/NextPageIcon/NextPageIcon.js";
import { PrevPageIcon } from "../../../../../components/icons/PrevPageIcon/PrevPageIcon.js";
import { TrackerHoursIcon } from "../../../../../components/icons/TrackerHoursIcon/TrackerHoursIcon.js";
import { AppointmentCancellationEnum } from "../../no-show-fee-list.types.js";
import "../../../../../components/icons/MainIcon/MainIcon.js";
const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50]
}));
const StyledTableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "Lato",
  fontWeight: "700",
  fontSize: "16px",
  lineHeight: "22px",
  letterSpacing: "0.5px",
  color: theme.palette.grey[800],
  padding: `11px ${theme.spacing(3)}`
}));
const StyledTableBodyCell = styled(TableCell)(({ theme }) => ({
  height: "52px",
  fontFamily: "Lato",
  fontWeight: "400",
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25px",
  color: theme.palette.grey[800],
  padding: `0 ${theme.spacing(3)}`,
  boxSizing: "border-box"
}));
const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary[800],
  cursor: "pointer"
}));
const CancellationReasonBadge = styled("div", {
  shouldForwardProp: (prop) => prop !== "reason"
})(({ reason, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  width: "fit-content",
  textAlign: "center",
  borderRadius: "16px",
  ...reason === AppointmentCancellationEnum.CANCELED_SHORT_TERM ? {
    backgroundColor: "#F4EBF5",
    color: theme.palette.highlight3[800]
  } : {
    backgroundColor: "#E3EFF5",
    color: theme.palette.highlight1[800]
  }
}));
const CancellationIcon = ({ reason }) => reason === AppointmentCancellationEnum.CANCELED_SHORT_TERM ? /* @__PURE__ */ jsx(TrackerHoursIcon, {}) : /* @__PURE__ */ jsx(EventBusyIcon, {});
const StatusDot = styled("div", {
  shouldForwardProp: (prop) => prop !== "isOpen"
})(({ isOpen, theme }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: isOpen ? theme.palette.secondary[900] : theme.palette.success[900],
  flexShrink: 0
}));
const StatusBadge = styled("div", {
  shouldForwardProp: (prop) => prop !== "isOpen"
})(({ isOpen, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  width: "fit-content",
  textAlign: "center",
  backgroundColor: isOpen ? theme.palette.secondary[50] : theme.palette.success[50],
  color: isOpen ? theme.palette.secondary[900] : theme.palette.success[900],
  borderRadius: "16px"
}));
const StyledCheckCircleIcon = styled(CheckCircleIcon)(({ theme }) => ({
  color: theme.palette.success[600],
  width: "24px",
  height: "24px"
}));
const StyledCancelIcon = styled(CancelIcon)(({ theme }) => ({
  color: theme.palette.error[600],
  width: "24px",
  height: "24px"
}));
const AGBIndicator = ({ value }) => value ? /* @__PURE__ */ jsx(StyledCheckCircleIcon, {}) : /* @__PURE__ */ jsx(StyledCancelIcon, {});
const PaginationWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  padding: theme.spacing(2),
  gap: theme.spacing(5)
}));
const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    fontFamily: "Lato",
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    letterSpacing: "0.25px",
    color: theme.palette.grey[800],
    border: `1px solid transparent`,
    "&:hover": {
      backgroundColor: theme.palette.primary[50],
      color: theme.palette.primary[800]
    },
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary[10],
      color: theme.palette.primary[800],
      borderColor: theme.palette.primary[800],
      "&:hover": {
        backgroundColor: theme.palette.primary[50]
      }
    }
  },
  "& .MuiPaginationItem-ellipsis": {
    "&:hover": {
      backgroundColor: "transparent"
    }
  },
  "& .MuiPaginationItem-previousNext, & .MuiPaginationItem-firstLast": {
    "&:hover": {
      backgroundColor: theme.palette.grey[100]
    }
  }
}));
const PageItemsInfo = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[800],
  textAlign: "right"
}));
const paginationIconStyles = ({ theme }) => ({
  "&&.MuiSvgIcon-root": {
    height: "12px",
    color: theme.palette.grey[800]
  }
});
const StyledFirstPageIcon = styled(FirstPageIcon)(({ theme }) => paginationIconStyles({ theme }));
const StyledLastPageIcon = styled(LastPageIcon)(({ theme }) => paginationIconStyles({ theme }));
const StyledNextPageIcon = styled(NextPageIcon)(({ theme }) => paginationIconStyles({ theme }));
const StyledPrevPageIcon = styled(PrevPageIcon)(({ theme }) => paginationIconStyles({ theme }));
export {
  AGBIndicator,
  CancellationIcon,
  CancellationReasonBadge,
  PageItemsInfo,
  PaginationWrapper,
  StatusBadge,
  StatusDot,
  StyledFirstPageIcon,
  StyledLastPageIcon,
  StyledLink,
  StyledNextPageIcon,
  StyledPagination,
  StyledPrevPageIcon,
  StyledTableBodyCell,
  StyledTableHead,
  StyledTableHeaderCell
};
